import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, wageQuestions, allowancesQuestions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function NewJerseyForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  formatFullName,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_name = formatFullName();

  const [claimExemptionFromWithholding, setClaimExemptionFromWithholding] = useState(data.claim_exemption_from_withholding || false);
  const [claimIncreaseWithholding, setClaimIncreaseWithholding] = useState(data.claim_increase_withholding || false);

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const wageForm = useRef();
  const step1WageQuestions = getInitialQuestions(wageQuestions);
  const [stepOneWageQuestions, setStepOneWageQuestions] = useState(step1WageQuestions);

  const allowancesForm = useRef();
  const allowancesQuestionsWithAnswers = getInitialQuestions(allowancesQuestions);
  const [stepOneAllowancesQuestions, setStepOneAllowancesQuestions] = useState(allowancesQuestionsWithAnswers);

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
    </div>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column mb="20px">
      <Text bold fontSize="14px" mb="12px">
        Exemption from withholding
      </Text>
      <S.QuestionWrapper>
        <Flex column>
          <label>
            <Flex mb="8px">
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={claimExemptionFromWithholding}
                checked={claimExemptionFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setClaimExemptionFromWithholding(e.target.checked);
                }}
              />
              <Box ml={2} />
              <Flex column>
                <Text mb="8px">
                  Claim exemption from withholding of NJ Gross Income Tax and certify that you have met one of the following conditions:
                </Text>
                <Text mb="5px">
                  {'\u2022'}
                  {' '}
                  Your filing status is SINGLE or MARRIED/CIVIL UNION PARTNER SEPARATE and your wages plus your taxable nonwage income will be $10,000 or less for the current year.
                </Text>
                <Text>
                  {'\u2022'}
                  {' '}
                  Your filing status is MARRIED/CIVIL UNION COUPLE JOINT, and your wages combined with your spouse’s/civil union partner’s wages plus your taxable nonwage income will be $20,000 or less for the current year.
                </Text>
                <Text>
                  {'\u2022'}
                  {' '}
                  Your filing status is HEAD OF HOUSEHOLD or QUALIFYING WIDOW(ER)/SURVIVING CIVIL UNION PARTNER and your wages plus your taxable nonwage income will be $20,000 or less for the current year.
                </Text>
              </Flex>
            </Flex>
          </label>
        </Flex>
      </S.QuestionWrapper>
    </Flex>
  );

  const renderIncreasedWithholding = (isReviewStep) => (
    <Flex column>
      <Text bold fontSize="14px" mb="12px">
        Increased withholding
      </Text>
      <Text mb="20px">
        If your wages will be taxed at a higher rate due to inclusion of other wages or income on your NJ-1040 return, you can choose to increase withholdings on your wages. It is not intended to provide withholding for other income or wages. If you need additional withholdings for other income or wages, use the additional amount section on this form.
      </Text>
      <Text mb="20px">
        Increased withholding applies to taxpayers who are married/civil union couple filing jointly, heads of households, or qualifying widow(er)/surviving civil union partner. Single individuals or married/civil union partners filing separate returns do not need to do this. If you have indicated a filing status of married/civil union couple filing jointly, head of household, or qualifying widow(er)/surviving civil union partner and your taxable income is greater than $50,000, you should strongly consider increasing your withholding.
      </Text>
      <S.QuestionWrapper mb="15px">
        <Flex column>
          <Flex column>
            <label>
              <Flex mb="8px">
                <S.Input
                  style={{ height: '22px' }}
                  type="radio"
                  value={!claimIncreaseWithholding}
                  checked={!claimIncreaseWithholding}
                  disabled={isReviewStep}
                  onChange={(e) => setClaimIncreaseWithholding(!e.target.checked)}
                />
                <Box ml={2} />
                Do not increase withholding
              </Flex>
            </label>
          </Flex>
          <Flex column>
            <label>
              <Flex mb="8px">
                <S.Input
                  style={{ height: '22px' }}
                  type="radio"
                  value={claimIncreaseWithholding}
                  checked={claimIncreaseWithholding}
                  disabled={isReviewStep}
                  onChange={(e) => setClaimIncreaseWithholding(e.target.checked)}
                />
                <Box ml={2} />
                Increase withholding
              </Flex>
            </label>
          </Flex>
        </Flex>
      </S.QuestionWrapper>

      { claimIncreaseWithholding && renderWageQuestions(isReviewStep) }
    </Flex>
  );

  const renderWageQuestions = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneWageQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneWageQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneWageQuestions(questions);
        }}
        ref={wageForm}
      />
    </div>
  );

  const renderAllowances = (isReviewStep) => (
    <Flex column mb="14px">
      <FormConsumer
        questions={stepOneAllowancesQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneAllowancesQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneAllowancesQuestions(questions);
        }}
        ref={allowancesForm}
      />
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {renderExemptionsFromWithholding()}
          {!claimExemptionFromWithholding && renderIncreasedWithholding()}
          {!claimExemptionFromWithholding && renderAllowances()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (wageForm && wageForm.current) {
              wageForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (allowancesForm && allowancesForm.current) {
              allowancesForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            const wageData = stepOneWageQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            const allowancesData = stepOneAllowancesQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

            const data = {
              ...stepOneData,
              ...wageData,
              ...allowancesData,
              claim_exemption_from_withholding: claimExemptionFromWithholding,
              claim_increase_withholding: claimIncreaseWithholding,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {renderExemptionsFromWithholding(true)}
        {!claimExemptionFromWithholding && renderIncreasedWithholding(true)}
        {!claimExemptionFromWithholding && renderAllowances(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default NewJerseyForm;
